/**
 * Utility functions for phone number processing
 */

export interface CallStat {
  label: string;
  count: number;
}

/**
 * Normalizes a phone number by removing the + prefix and any non-digit characters
 * @param phoneNumber - The phone number to normalize
 * @returns The normalized phone number (digits only)
 */
export const normalizePhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.replace(/[^\d]/g, "");
};

/**
 * Combines call stats by normalizing phone numbers and merging counts
 * Phone numbers with the same digits (with or without + prefix) will be combined
 * @param callStats - Array of call statistics
 * @returns Combined call statistics with + prefix preserved
 */
export const combinePhoneNumberStats = (callStats: CallStat[]): CallStat[] => {
  const combinedMap = new Map<string, number>();

  callStats.forEach((stat) => {
    const normalizedNumber = normalizePhoneNumber(stat.label);
    const existingCount = combinedMap.get(normalizedNumber) || 0;
    combinedMap.set(normalizedNumber, existingCount + stat.count);
  });

  // Convert map back to array, add + prefix, and sort by count (descending)
  return Array.from(combinedMap.entries())
    .map(([label, count]) => ({ label: `+${label}`, count }))
    .sort((a, b) => b.count - a.count);
};
